import type { DatabaseEngine } from './connectionFormSchema';

/**
 * Per-engine default values that the connection form fills into an empty field
 * when the user tabs out of it (blur). These are the single source of truth for
 * BOTH the blur-fill handlers in `ConnectionFormFields` and the "Press Tab to
 * fill '<default>'" validation hint — so the hint can never disagree with what
 * Tab actually fills.
 */
export const DEFAULT_SERVERS: Partial<Record<DatabaseEngine, string>> = {
  mssql: 'localhost\\SQLEXPRESS',
  pgsql: 'localhost',
  sqlite: '',
  mysql: 'localhost',
  oracle: 'localhost',
  kusto: 'https://mycluster.region.kusto.windows.net',
};

export const DEFAULT_LOGINS: Partial<Record<DatabaseEngine, string>> = {
  mssql: '',
  pgsql: 'postgres',
  sqlite: '',
  mysql: 'root',
  oracle: 'system',
};

export const DEFAULT_DATABASES: Partial<Record<DatabaseEngine, string>> = {
  mssql: '',
  pgsql: 'postgres',
  sqlite: '',
  mysql: '',
  oracle: '',
};

/** Form fields that have a blur-fillable per-engine default. */
export type DefaultableField = 'server' | 'login' | 'database';

/**
 * The value the form's blur handler would fill into an empty `field` for the
 * given `engine`, or `undefined` when there is no such default.
 *
 * `server` is special-cased to match the blur handler exactly:
 * - `sqlite` has no literal default (the server field is a file path).
 * - `kusto` blur *normalizes* the entered URL rather than filling the
 *   placeholder template, so it must not advertise a fill default.
 */
export function getConnectionFieldDefault(
  engine: DatabaseEngine,
  field: DefaultableField
): string | undefined {
  if (field === 'server') {
    if (engine === 'sqlite' || engine === 'kusto') return undefined;
    return DEFAULT_SERVERS[engine] || undefined;
  }
  if (field === 'login') {
    return DEFAULT_LOGINS[engine] || undefined;
  }
  return DEFAULT_DATABASES[engine] || undefined;
}
